import BarChart from "@rio-cloud/rio-uikit/BarChart";
import Bar from "@rio-cloud/rio-uikit/Bar";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../../enums/occurrenceStep";
import { formatMilisecondsAdaptive, formatMilisecondsInDaysHours, formatMilisecondsInDaysHoursMinutes } from "../../../../utils/dateUtils";
import { StepChart } from "./stepChart";
import { OccurrenceWithTimeSpent } from "../../../../models/occurrence";

export interface AverageTimeChartProps {
  showAverageTimeChart: boolean;
  setShowAverageTimeChart: (showAverageTimeChart: boolean) => void;
  activeTab: string;
  setActiveTab: (activeTab: string | null) => void;
  contentWithTooltip: (value: string) => any;
  occurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  finalizedOccurrencesBySteps?: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  adjustDataWithMinHeight: (data: any[], minHeightPercentage: number) => any[];
  isResponsiveView: boolean;
  chartWidthMultiplier: number;
  defaultChartMinWidth: number;
  dynamicBarSize: number;
  CustomTick: (props: any) => JSX.Element;
  occurrenceStepAvailable: string[];
  stepFilter: OccurrenceStepEnum | null;
  loading?: boolean;
  firstLoad: boolean;
  setChassisListFilter: (chassisListFilter: string[]) => void;
  setStepFilter: (stepFilter: OccurrenceStepEnum | null) => void;
  setChassisListFilterByStep: (chassisListFilterByStep: string[]) => void;
  finishedFilter: string[];
  divideByUniqueChassis?: boolean;
  isCurrentStageMode?: boolean;
  setCurrentStepFilter?: (step: OccurrenceStepEnum | null) => void;
}

export const AverageTimeChart = (props: AverageTimeChartProps) => {
  const isFinalized = props.finishedFilter.length === 1 && props.finishedFilter.includes('true');

  const getStepData = (stepKey: OccurrenceStepEnum) =>
    isFinalized
      ? (props.finalizedOccurrencesBySteps?.[stepKey] ?? [])
      : (props.occurrencesBySteps[stepKey] ?? []);

  const CustomLabel = ({ x, y, width, index }: any) => {
    const stepKey = props.activeTab as OccurrenceStepEnum;
    const originalTime = getStepData(stepKey)[index]?.steps[stepKey]?.total ?? 0;
    const label = formatMilisecondsAdaptive(originalTime * 60000);
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill="#a7afbb"
        fontSize={props.isResponsiveView ? "12px" : "16px"}
      >
        {label}
      </text>
    );
  };

  return (
    <>
      <span className="flex w-full justify-between text-size-16 font-semibold p-8">
        <div>Tempo médio por etapa</div>
        <div
          className="cursor-pointer"
          onClick={() => props.setShowAverageTimeChart(!props.showAverageTimeChart)}
        >
          <span className={`rioglyph rioglyph-chevron-${props.showAverageTimeChart ? "up" : "down"}`} />
        </div>
      </span>

      {props.showAverageTimeChart &&
        (
          <div className="flex items-center overflow-x-auto flex-nowrap pb-2 mb-4 -mx-4 px-4 snap-x snap-mandatory gap-3">
            {props.occurrenceStepAvailable.map((stepEnum) => {
              const stepKey = OccurrenceStepEnum[stepEnum as keyof typeof OccurrenceStepEnum];
              const isActive = props.activeTab === stepKey;
              const stepData = getStepData(stepKey);
              const totalTimeInSeconds = stepData.reduce((acc, curr) => acc + (curr.steps[stepKey]?.total ?? 0), 0);
              const divisor = props.divideByUniqueChassis
                ? new Set(stepData.map((o) => o.chassis)).size
                : stepData.length;
              const valueInMiliseconds = (totalTimeInSeconds / (divisor || 1)) * 1000;

              return (
                <div
                  key={stepEnum}
                  onClick={() => {
                    if (props.isCurrentStageMode) {
                      if (props.activeTab === stepKey) {
                        props.setActiveTab('');
                        props.setCurrentStepFilter?.(null);
                      } else {
                        props.setActiveTab(stepKey);
                        props.setCurrentStepFilter?.(stepKey);
                      }
                    } else {
                      if (props.activeTab === stepKey) {
                        props.setActiveTab('');
                        props.setStepFilter(null);
                      } else {
                        props.setActiveTab(stepKey);
                        props.setStepFilter(stepKey);
                      }
                    }
                  }}
                  className={
                    `flex flex-col justify-center border-b-2 border-t-2 text-left transition bg-gray-100 dark:bg-neutral-800 h-[65px] px-4 snap-start
                        cursor-pointer
                        ${props.isResponsiveView ? "flex-none min-w-[150px]" : "flex-1"}
                        ${isActive ? "bg-white dark:bg-neutral-900 text-color-primary font-semibold border-b-4 border-b-[#30B4C0]" : "hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-500 dark:text-neutral-400"}
                        ${stepEnum !== props.occurrenceStepAvailable[props.occurrenceStepAvailable.length - 1] ? "border-r-[1px]" : ""}`}
                >
                  <div className="text-size-12 font-bold line-clamp-2 h-[25px] ">
                    {OccurrenceStepLabelEnum[stepEnum as keyof typeof OccurrenceStepLabelEnum]}
                  </div>
                  <div className="font-bold text-size-16 truncate mt-2 ">
                    {props.loading && props.firstLoad ?
                      '-' :
                      stepData.length > 0
                        ? props.contentWithTooltip(formatMilisecondsAdaptive(valueInMiliseconds))
                        : '0m'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* {props.showAverageTimeChart && props.activeTab === '' && (
        <div className="w-full h-full overflow-x-auto px-8 py-4">
          <StepChart
            adjustDataWithMinHeight={props.adjustDataWithMinHeight}
            data={props.occurrencesBySteps}
            finalizedData={props.finalizedOccurrencesBySteps}
            chartWidthMultiplier={props.chartWidthMultiplier}
            defaultChartMinWidth={props.defaultChartMinWidth}
            dynamicBarSize={props.dynamicBarSize}
            isResponsiveView={props.isResponsiveView}
            loading={props.loading}
            metric="averageTime"
          />
        </div>
      )} */}

    </>

  );
};
